// cspell:disable
import type * as zod from "zod";
import { array, object, optional, string, union, enum as zenum } from "zod";

export const fieldValueTypeSchema = zenum(["string", "number"]);
export type FieldValueType = zod.infer<typeof fieldValueTypeSchema>;

// Level 3 (deepest): only string
const fieldValueLevel3Schema = string();

// Level 3 entry: key with string value
const fieldEntryLevel3Schema_ = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel3Schema,
  valueType: optional(fieldValueTypeSchema),
});
export const fieldEntryLevel3Schema: zod.ZodType<FieldEntryLevel3> =
  fieldEntryLevel3Schema_;
export interface FieldEntryLevel3 extends zod.infer<
  typeof fieldEntryLevel3Schema_
> {}

// Level 2: string or array of level 3 entries
const fieldValueLevel2Schema_ = union([
  string(),
  array(fieldEntryLevel3Schema),
]);
export const fieldValueLevel2Schema: zod.ZodType<FieldValueLevel2> =
  fieldValueLevel2Schema_;
export type FieldValueLevel2 = string | FieldEntryLevel3[];

// Level 2 entry: key with level 2 value
const fieldEntryLevel2Schema_ = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel2Schema,
  valueType: optional(fieldValueTypeSchema),
});
export const fieldEntryLevel2Schema: zod.ZodType<FieldEntryLevel2> =
  fieldEntryLevel2Schema_;
export interface FieldEntryLevel2 extends zod.infer<
  typeof fieldEntryLevel2Schema_
> {}

// Level 1 (top level): string or array of level 2 entries
const fieldValueLevel1Schema_ = union([
  string(),
  array(fieldEntryLevel2Schema),
]);
export const fieldValueLevel1Schema: zod.ZodType<FieldValueLevel1> =
  fieldValueLevel1Schema_;
export type FieldValueLevel1 = string | FieldEntryLevel2[];

// Top-level schema for custom document fields (used on RMAs, credit memos, etc.)
export const netSuiteCustomDocumentFieldSchema = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel1Schema,
  valueType: optional(fieldValueTypeSchema),
});

// Top-level schema for custom document fields on line items
export const netSuiteCustomDocumentFieldItemSchema = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel1Schema,
  valueType: optional(fieldValueTypeSchema),
});

// Validation versions with min length checks
const fieldEntryLevel3SchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: string().min(1, "Field value is required"),
  valueType: optional(fieldValueTypeSchema),
});

const fieldValueLevel2SchemaWithValidation: zod.ZodType<FieldValueLevel2> =
  union([
    string().min(1, "Field value is required"),
    array(fieldEntryLevel3SchemaWithValidation),
  ]);

const fieldEntryLevel2SchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: fieldValueLevel2SchemaWithValidation,
  valueType: optional(fieldValueTypeSchema),
});

const fieldValueLevel1SchemaWithValidation: zod.ZodType<FieldValueLevel1> =
  union([
    string().min(1, "Field value is required"),
    array(fieldEntryLevel2SchemaWithValidation),
  ]);

export const netSuiteCustomDocumentFieldSchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: fieldValueLevel1SchemaWithValidation,
  valueType: optional(fieldValueTypeSchema),
});

export const netSuiteCustomDocumentFieldItemSchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: fieldValueLevel1SchemaWithValidation,
  valueType: optional(fieldValueTypeSchema),
});

export const netSuiteCustomLineItemOnRMASchema = object({
  itemId: string(),
  locationId: string(),
  rateTemplate: string(),
  amountTemplate: string(),
});

export const netSuiteCustomLineItemOnRMASchemaWithValidation = object({
  itemId: string().min(1, "Item ID is required"),
  locationId: string().min(1, "Location ID is required"),
  rateTemplate: string().min(1, "Rate template is required"),
  amountTemplate: string().min(1, "Amount template is required"),
});

export const netsuiteProcessingCustomFieldSchema = object({
  fieldName: string().min(1, "Field name is required"),
  values: array(string()),
});

export const netsuiteRMACustomizationSchema = object({
  customFieldsOnRMA: array(netSuiteCustomDocumentFieldSchema).optional(),
  netSuiteCustomLineItemsOnRMAS: array(
    netSuiteCustomLineItemOnRMASchema,
  ).optional(),
  customFieldsOnLineItem: array(
    netSuiteCustomDocumentFieldItemSchema,
  ).optional(),
});

export const netsuiteRMACustomizationSchemaWithValidation = object({
  customFieldsOnRMA: array(
    netSuiteCustomDocumentFieldSchemaWithValidation,
  ).optional(),
  netSuiteCustomLineItemsOnRMAS: array(
    netSuiteCustomLineItemOnRMASchemaWithValidation,
  ).optional(),
  customFieldsOnLineItem: array(
    netSuiteCustomDocumentFieldItemSchemaWithValidation,
  ).optional(),
});

export const netsuiteCreditMemoCustomizationSchema = object({
  customFieldsOnCreditMemo: array(netSuiteCustomDocumentFieldSchema).optional(),
});

export const netsuiteCreditMemoCustomizationSchemaWithValidation = object({
  customFieldsOnCreditMemo: array(
    netSuiteCustomDocumentFieldSchemaWithValidation,
  ).optional(),
});

export const netsuiteCashRefundCustomizationSchema = object({
  customFieldsOnCashRefund: array(netSuiteCustomDocumentFieldSchema).optional(),
});

export const netsuiteCashRefundCustomizationSchemaWithValidation = object({
  customFieldsOnCashRefund: array(
    netSuiteCustomDocumentFieldSchemaWithValidation,
  ).optional(),
});
